import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Upload, Download } from 'lucide-react';
import { parseCsvPriceList, downloadPricelistTemplate, uploadPricelistInBatches } from './csvUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BulkPricelistUpload = ({ hospitals, currencies, axiosConfig, onUploaded }) => {
  const [hospitalName, setHospitalName] = useState('');
  const [csvData, setCsvData] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(null);

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    const reader = new FileReader();
    reader.onload = (event) => {
      setCsvData(event.target.result);
      toast.success(`File "${f.name}" loaded successfully`);
    };
    reader.readAsText(f);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!hospitalName) {
      toast.error('Please select a hospital first');
      return;
    }
    setLoading(true);
    try {
      const items = parseCsvPriceList(csvData);
      if (items.length === 0) {
        toast.error('No valid items found in CSV');
        return;
      }

      const { created, skipped } = await uploadPricelistInBatches(API, hospitalName, items, axiosConfig, (b, total) => setProgress({ b, total }));
      toast.success(`✅ Created ${created} items, skipped ${skipped} duplicates`);
      setHospitalName('');
      setCsvData('');
      setFile(null);
      onUploaded();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload price list');
    } finally {
      setLoading(false);
      setProgress(null);
    }
  };

  const selectedHospital = hospitals.find((h) => h.hospital_name === hospitalName);
  const selectedCurrency = currencies[selectedHospital?.currency_code || 'USD'];

  return (
    <Card className="border-purple-200 shadow-md">
      <CardHeader className="bg-gradient-to-r from-purple-50 to-purple-100 border-b border-purple-200">
        <CardTitle className="flex items-center gap-2 text-purple-900">
          <Upload className="w-5 h-5" />
          Bulk Upload Price List (CSV)
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-6">
        <form onSubmit={handleUpload} className="space-y-4">
          <div className="space-y-2">
            <Label>Hospital Name</Label>
            <Select value={hospitalName} onValueChange={setHospitalName}>
              <SelectTrigger data-testid="bulk-pricelist-hospital-select">
                <SelectValue placeholder="Select hospital" />
              </SelectTrigger>
              <SelectContent>
                {hospitals.map((hospital) => {
                  const currency = currencies[hospital.currency_code || 'USD'];
                  return (
                    <SelectItem key={hospital.hospital_name} value={hospital.hospital_name}>
                      {hospital.hospital_name} ({currency?.symbol || '$'} {currency?.code || 'USD'})
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            {hospitalName && (
              <p className="text-xs text-blue-600 bg-blue-50 p-2 rounded">
                💡 All prices will be in <strong>{`${selectedCurrency?.name || 'US Dollar'} (${selectedCurrency?.symbol || '$'})`}</strong>
              </p>
            )}
          </div>

          <div className="border rounded-lg p-4 bg-purple-50">
            <h3 className="font-semibold mb-2 text-sm">Step 1: Download Template</h3>
            <p className="text-xs text-gray-600 mb-3">
              Download the CSV template and open it in Microsoft Excel to fill in your price list data.
            </p>
            <Button type="button" onClick={downloadPricelistTemplate} variant="outline" className="w-full" data-testid="download-pricelist-template">
              <Download className="w-4 h-4 mr-2" />
              Download Price List Template
            </Button>
            <p className="text-xs text-gray-500 mt-2">Format: ITEM ID, ITEM NAME, ITEM TYPE, UoM (optional), COST</p>
          </div>

          <div className="space-y-2">
            <Label>Step 2: Upload CSV File</Label>
            <Input type="file" accept=".csv" onChange={handleFileChange} className="cursor-pointer" data-testid="pricelist-csv-input" />
            {file && <p className="text-xs text-green-600">✓ File loaded: {file.name}</p>}
          </div>

          <Button
            data-testid="bulk-upload-pricelist-button"
            type="submit"
            disabled={loading || !csvData}
            className="w-full bg-purple-600 hover:bg-purple-700"
          >
            {loading ? (progress ? `Uploading batch ${progress.b} of ${progress.total}…` : 'Uploading...') : 'Upload Price List'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};
